import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface DocumentAttachment { path: string; filename: string; mime: string; bytes: number; url?: string }
export const mediaAttributes = {
  attachment: { default: null,
    parseHTML: (el: HTMLElement) => { try { const a = JSON.parse(el.getAttribute('data-attachment') || 'null'); return a && typeof a.path === 'string' && typeof a.filename === 'string' ? a : null; } catch { return null; } },
    renderHTML: (attrs: Record<string, unknown>) => attrs.attachment ? { 'data-attachment': JSON.stringify(attrs.attachment) } : {},
  },
  displayMode: { default: null, parseHTML: (el: HTMLElement) => el.getAttribute('data-display-mode'), renderHTML: (a: Record<string, unknown>) => a.displayMode ? { 'data-display-mode': a.displayMode } : {} },
  displayWidth: { default: 360, parseHTML: (el: HTMLElement) => Number(el.getAttribute('data-display-width')) || 360, renderHTML: (a: Record<string, unknown>) => ({ 'data-display-width': a.displayWidth }) },
  displayHeight: { default: 260, parseHTML: (el: HTMLElement) => Number(el.getAttribute('data-display-height')) || 260, renderHTML: (a: Record<string, unknown>) => ({ 'data-display-height': a.displayHeight }) },
};
type Pending = { id: string; pos: number; error?: string };
const key = new PluginKey<Pending[]>('gam-media-drop');
export const MediaDrop = Extension.create<{ upload: () => ((file: File) => Promise<DocumentAttachment>) | undefined }>({
  name: 'mediaDrop',
  priority: 1000,
  addOptions: () => ({ upload: () => undefined }),
  addProseMirrorPlugins() {
    const editor = this.editor, upload = this.options.upload;
    return [new Plugin<Pending[]>({
      key,
      state: {
        init: () => [],
        apply(tr, previous) {
          let next = previous.map(p => ({ ...p, pos: tr.mapping.map(p.pos, 1) }));
          const event = tr.getMeta(key);
          if (event?.add) next.push(event.add);
          if (event?.remove) next = next.filter(p => p.id !== event.remove);
          if (event?.error) next = next.map(p => p.id === event.id ? { ...p, error: event.error } : p);
          return next;
        },
      },
      props: {
        decorations: state => DecorationSet.create(state.doc, (key.getState(state) || []).map(p => Decoration.widget(p.pos, () => {
          const el = document.createElement('span'); el.className = 'gam-upload-status'; el.contentEditable = 'false';
          el.textContent = p.error || 'מעלה קובץ…'; el.setAttribute('role', p.error ? 'alert' : 'status');
          if (p.error) el.onclick = () => editor.view.dispatch(editor.state.tr.setMeta(key, { remove: p.id }));
          return el;
        }, { key: `${p.id}-${p.error || ''}` }))),
        handleDOMEvents: { drop(view, event) {
          const files = Array.from(event.dataTransfer?.files || []), handler = upload();
          if (!files.length || view.dragging) return false;
          event.preventDefault();
          if (!handler || !editor.isEditable) return true;
          const free = view.dom.classList.contains('gam-free-position-active');
          const pos = free ? view.state.doc.content.size : view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.doc.content.size;
          const id = crypto.randomUUID();
          view.dispatch(view.state.tr.setMeta(key, { add: { id, pos } }).setMeta('addToHistory', false));
          const root = view.dom.getBoundingClientRect(), scale = root.width / view.dom.offsetWidth || 1;
          void (async () => {
            try {
              for (const [index, file] of files.entries()) {
                const attachment = await handler(file);
                if (editor.isDestroyed || !editor.isEditable) return;
                const anchor = key.getState(view.state)?.find(p => p.id === id);
                if (!anchor) return;
                const image = file.type.startsWith('image/');
                const attrs = { attachment, src: attachment.url || '', url: attachment.url || '', filename: file.name, alt: file.name, displayMode: image ? 'preview' : 'file',
                  ...(free ? { freePosition: { x: Math.max(0, Math.min((event.clientX - root.left) / scale, view.dom.clientWidth - 360)), y: Math.max(26, (event.clientY - root.top) / scale + index * 40), width: 360 } } : {}) };
                // Tiptap handles insertion into/around the current schema node.
                if (!editor.commands.insertContentAt(anchor.pos, { type: image ? 'imageBlock' : 'fileBlock', attrs })) throw new Error('הקובץ הועלה אך לא ניתן לשלב אותו כאן');
              }
              view.dispatch(view.state.tr.setMeta(key, { remove: id }).setMeta('addToHistory', false));
            } catch (error) {
              if (!editor.isDestroyed) view.dispatch(view.state.tr.setMeta(key, { id, error: error instanceof Error ? error.message : 'העלאת הקובץ נכשלה' }).setMeta('addToHistory', false));
            }
          })();
          return true;
        } },
      },
    })];
  },
});
